/**
 * Microphone capture for dictation. public/pcm-worklet.js turns the mic into
 * PCM16 mono 24 kHz frames (what the transcription socket expects), and an
 * analyser gives the input level for silence detection.
 */

export const PCM_WORKLET_URL = "/pcm-worklet.js";

export function isVoiceInputSupported(): boolean {
  return (
    typeof navigator !== "undefined" &&
    typeof navigator.mediaDevices?.getUserMedia === "function" &&
    typeof WebSocket !== "undefined" &&
    typeof AudioContext !== "undefined" &&
    typeof AudioWorkletNode !== "undefined"
  );
}

export function requestMicrophone(): Promise<MediaStream> {
  return navigator.mediaDevices.getUserMedia({ audio: true });
}

export interface AudioCapture {
  /** Input level now, from 0 (silence) to 1. */
  level: () => number;
  /** Disconnect the audio graph (the caller owns the stream and the context). Safe to call twice. */
  stop: () => void;
}

export async function connectCapture(
  context: AudioContext,
  stream: MediaStream,
  onAudio: (chunk: ArrayBuffer) => void,
): Promise<AudioCapture> {
  await context.audioWorklet.addModule(PCM_WORKLET_URL);
  const source = context.createMediaStreamSource(stream);
  const analyser = context.createAnalyser();
  analyser.fftSize = 256;
  analyser.smoothingTimeConstant = 0.3;
  const worklet = new AudioWorkletNode(context, "pcm-processor");
  worklet.port.onmessage = (event: MessageEvent<ArrayBuffer>) => onAudio(event.data);
  // The worklet only posts frames; a muted path to the output keeps the graph running without echo.
  const mute = context.createGain();
  mute.gain.value = 0;
  source.connect(analyser);
  source.connect(worklet);
  worklet.connect(mute).connect(context.destination);
  if (context.state === "suspended") await context.resume();

  const bins = new Uint8Array(analyser.frequencyBinCount);
  let stopped = false;
  return {
    level: () => {
      analyser.getByteFrequencyData(bins);
      return bins.reduce((sum, value) => sum + value, 0) / bins.length / 255;
    },
    stop: () => {
      if (stopped) return;
      stopped = true;
      worklet.port.onmessage = null;
      worklet.disconnect();
      source.disconnect();
    },
  };
}
